// components/mobile/MobileActionDetail.jsx
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Alert, Button, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { ProductThumb } from './ProductThumb';
import { DetailRows } from './DetailRows';
import { formatDate, integer, money, displayName } from '../../utils/mobileHelpers';

export const MobileActionDetail = ({ detailToken, domain, headers, dates, onBack, onOpenDetail }) => {
  const [state, setState] = useState({ loading: true, data: null, error: '' });
  const [type, id] = String(detailToken || '').split(':');

  useEffect(() => {
    if (!type || !id) return undefined;
    const controller = new AbortController();
    setState({ loading: true, data: null, error: '' });
    
    axios.get(`${domain}/api/mobile-actions/details/${type}/${id}`, { 
      headers, 
      params: dates, 
      signal: controller.signal 
    })
    .then((response) => setState({ loading: false, data: response.data, error: '' }))
    .catch((error) => { 
      if (error?.code !== 'ERR_CANCELED') {
        setState({ loading: false, data: null, error: error?.response?.data?.error || 'Impossible de charger ce détail.' });
      }
    });
    
    return () => controller.abort();
  }, [dates, detailToken, domain, headers, id, type]);

  const record = state.data?.record || {};
  const title = state.data?.kind === 'vente' ? `Vente #${record.id_vente || id}` : 
                state.data?.kind === 'commande' ? `Commande #${record.id_commande || id}` : 
                state.data?.kind === 'livraison' ? `Livraison #${record.id_detail_livraison || id}` : 
                state.data?.kind === 'livreur' ? displayName(record.delivery_person) : 
                state.data?.kind === 'client' ? displayName(record.nom) : 
                state.data?.kind === 'variante' ? displayName(record.nom_produit) : 
                state.data?.kind === 'reception' ? `Réception #${record.id_reception || id}` : 
                `Mouvement #${record.id_mouvement || id}`;

  const renderFact = (condition, label, value) => {
    if (!condition) return null;
    return (
      <div>
        <small>{label}</small>
        <strong>{value}</strong>
      </div>
    );
  };

  const renderClientInfo = () => {
    if (!record.client_name) return null;
    return (
      <div>
        <small>Cliente</small>
        <strong>{record.client_name}</strong>
        <em>{record.client_phone || 'Téléphone non renseigné'}</em>
      </div>
    );
  };

  const renderLivreurInfo = () => {
    if (state.data?.kind !== 'livreur') {
      if (record.delivery_person) {
        return (
          <div>
            <small>Livreur</small>
            <strong>{record.delivery_person}</strong>
            <em>{record.delivery_phone || ''}</em>
          </div>
        );
      }
      return null;
    }
    
    return (
      <>
        <div><small>Commandes confiées</small><strong>{integer(record.assigned_orders)}</strong></div>
        <div><small>Paires confiées</small><strong>{integer(record.assigned_pairs)}</strong></div>
        <div><small>Paires nettes vendues</small><strong>{integer(record.net_sold)}</strong></div>
        <div><small>Retours cliente</small><strong>{integer(record.customer_returns)}</strong></div>
        <div><small>Retours livraison</small><strong>{integer(record.delivery_returns)}</strong></div>
        <div><small>En circulation</small><strong>{integer(record.in_transit)}</strong></div>
        <div><small>Conversion</small><strong>{integer(record.conversion_percent)} %</strong></div>
        <div><small>Montant des ventes</small><strong>{money(record.sales_amount)}</strong></div>
      </>
    );
  };

  const renderDetailSections = () => {
    const kind = state.data?.kind;
    if (kind === 'vente') {
      return <DetailRows title="Détail de la commande" rows={state.data.order_lines} domain={domain} onOpen={onOpenDetail} />;
    }
    if (kind === 'commande') {
      return (
        <>
          <DetailRows title="Paires de la commande" rows={state.data.lines} domain={domain} onOpen={onOpenDetail} />
          <DetailRows title="Ventes confirmées liées" rows={state.data.related_sales} domain={domain} onOpen={onOpenDetail} />
        </>
      );
    }
    if (kind === 'client') {
      return (
        <>
          <DetailRows title="Dernières ventes" rows={state.data.recent_sales} domain={domain} onOpen={onOpenDetail} />
          <DetailRows title="Dernières commandes" rows={state.data.recent_orders} domain={domain} onOpen={onOpenDetail} />
        </>
      );
    }
    if (kind === 'variante') {
      return <DetailRows title="Historique de la combinaison" rows={state.data.activity} domain={domain} onOpen={onOpenDetail} />;
    }
    if (kind === 'livreur') {
      return (
        <>
          <DetailRows title="Dernières ventes confirmées" rows={state.data.recent_sales} domain={domain} onOpen={onOpenDetail} />
          <DetailRows title="Dernières commandes confiées" rows={state.data.recent_deliveries} domain={domain} onOpen={onOpenDetail} />
        </>
      );
    }
    return null;
  };

  return (
    <section className="mobile-action-page mobile-action-detail-page">
      <div className="mobile-action-shell">
        <header className="mobile-action-header">
          <Button className="mobile-action-back" shape="circle" icon={<ArrowLeftOutlined />} onClick={onBack} />
          <div>
            <span>NDOÉ MOBILE · DÉTAIL LECTURE SEULE</span>
            <h1>{title}</h1>
            <p>Les données de gestion restent protégées : aucune modification n'est possible ici.</p>
          </div>
        </header>
        
        {state.loading ? (
          <div className="mobile-action-loading">
            <Spin size="large" />
            <span>Chargement du détail…</span>
          </div>
        ) : state.error ? (
          <Alert type="warning" showIcon message={state.error} />
        ) : (
          <>
            <section className="mobile-detail-hero">
              {/* 76 px ici (.mobile-detail-hero .mobile-action-thumb), pas 42. */}
              <ProductThumb domain={domain} image={record.img} title={title} largeur={76} />
              <div>
                <span>{state.data?.kind?.toUpperCase() || 'DÉTAIL'}</span>
                <h2>{record.nom_produit || record.delivery_person || record.client_name || record.nom || title}</h2>
                <p>{[record.couleur, record.taille ? `P${record.taille}` : null].filter(Boolean).join(' · ') || record.client_phone || record.delivery_phone || record.type_mouvement || 'Information disponible dans Ndoé App'}</p>
              </div>
            </section>
            
            <section className="mobile-detail-facts">
              {renderFact(record.date_vente, 'Date de vente', formatDate(record.date_vente, true))}
              {renderFact(record.date_commande, 'Date de commande', formatDate(record.date_commande, true))}
              {renderFact(record.date_creation, 'Date de création', formatDate(record.date_creation, true))}
              {renderFact(record.date_mouvement, 'Date du mouvement', formatDate(record.date_mouvement, true))}
              {renderFact(record.created_at, 'Date de réception', formatDate(record.created_at, true))}
              {renderFact(record.quantity !== undefined, 'Quantité', `${integer(record.quantity)} paire(s)`)}
              {renderFact(record.quantite !== undefined, 'Quantité', `${integer(record.quantite)} paire(s)`)}
              {renderFact(record.amount !== undefined, 'Montant', money(record.amount))}
              {renderFact(record.prix_unitaire !== undefined, 'Prix unitaire', money(record.prix_unitaire))}
              {renderFact(record.stock !== undefined, 'Stock actuel', `${integer(record.stock)} paire(s)`)}
              {renderClientInfo()}
              {renderLivreurInfo()}
              {record.description && (
                <div className="is-wide">
                  <small>Commentaire</small>
                  <strong>{record.description}</strong>
                </div>
              )}
            </section>
            
            {renderDetailSections()}
          </>
        )}
      </div>
    </section>
  );
};